import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import api from './api';
import { Icons } from './Icons';
import { ItemFormPage, ScannerPage, UsersPage, CategoriesPage } from './Pages';
import APP_VERSION from './version';

const STATUS_MAP = {
  available: 'Verfügbar', defect: 'Defekt', repair: 'In Reparatur',
  lent: 'Verliehen', for_sale: 'Zu verkaufen', sold: 'Verkauft',
  to_dispose: 'Zu entsorgen', disposed: 'Entsorgt'
};
const ROLE_LABELS = { admin: 'Admin', editor: 'Bearbeiter', readonly: 'Nur-Lesen' };

// === Context ===
const AuthContext = createContext(null);
const ToastContext = createContext(null);
export function useAuth() { return useContext(AuthContext); }
export function useToast() { return useContext(ToastContext); }

// === Toast ===
function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const addToast = useCallback((msg, type = 'success') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, msg, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 4000);
  }, []);
  return (
    <ToastContext.Provider value={addToast}>
      {children}
      <div className="toast-container">
        {toasts.map(t => <div key={t.id} className={`toast toast-${t.type}`}>{t.msg}</div>)}
      </div>
    </ToastContext.Provider>
  );
}

// === Auth ===
function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/auth/me')
      .then(res => { setUser(res.data); setLoading(false); })
      .catch(() => { setUser(null); setLoading(false); });
  }, []);

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    window.location.href = '/auth/login?reauth=1';
  };

  if (loading) return <div className="login-page"><div className="spinner" /></div>;
  return <AuthContext.Provider value={{ user, logout }}>{children}</AuthContext.Provider>;
}

function ProtectedRoute({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" />;
  return children;
}

// === Login ===
function LoginPage() {
  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-logo-area">
          <img src="/logo-chp.svg" alt="CHP Gruppe" className="login-logo" />
        </div>
        <div className="login-title">Inventar</div>
        <div className="login-subtitle">Anmeldung mit Microsoft 365</div>
        <div style={{padding: '0 40px 40px'}}>
          <a href="/auth/login" className="btn btn-primary btn-lg" style={{width:'100%', textDecoration:'none', display:'flex', alignItems:'center', justifyContent:'center', gap:10}}>
            <svg width="20" height="20" viewBox="0 0 23 23" aria-hidden="true">
              <rect width="10" height="10" x="1" y="1" fill="#f25022"/>
              <rect width="10" height="10" x="12" y="1" fill="#7fba00"/>
              <rect width="10" height="10" x="1" y="12" fill="#00a4ef"/>
              <rect width="10" height="10" x="12" y="12" fill="#ffb900"/>
            </svg>
            Mit Microsoft anmelden
          </a>
          <div style={{textAlign:'center',marginTop:16}}>
            <a href="mailto:admin@chp-gruppe.com?subject=Inventar%20–%20Zugang%20anfordern&body=Hallo%2C%0A%0Aich%20benötige%20Zugang%20zur%20Inventar-App.%0A%0AMein%20Name%3A%20%0A%0AVielen%20Dank!"
              style={{color:'var(--text-muted)',fontSize:'0.85rem',textDecoration:'none'}}>Zugang anfordern</a>
          </div>
        </div>
      </div>
    </div>
  );
}

// === Dashboard ===
function DashboardPage() {
  const [stats, setStats] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  useEffect(() => { api.get('/stats').then(r => setStats(r.data)); }, []);
  if (!stats) return <div className="page-container"><div className="loading-page"><div className="spinner" /></div></div>;
  const canEdit = user && (user.role === 'admin' || user.role === 'editor');

  return (
    <div className="page-container">
      <div className="page-header">
        <div><h1 className="page-title">Dashboard</h1><p className="page-subtitle">Übersicht der Inventarverwaltung</p></div>
        <div style={{display:'flex',alignItems:'center',gap:16}}>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => navigate('/items/new')}>
              <Icons.Plus /> Neues Item
            </button>
          )}
          <div style={{fontSize:'0.75rem',color:'var(--text-muted)',fontFamily:'var(--font-mono)'}}>v {APP_VERSION}</div>
        </div>
      </div>
      <div className="stats-grid">
        <div className="stat-card"><div className="stat-label">Gesamt Items</div><div className="stat-value">{stats.total}</div></div>
        <div className="stat-card stat-card-clickable" onClick={() => navigate('/items?unassigned=1')} style={{cursor:'pointer'}}><div className="stat-label">Noch zuzuordnen</div><div className="stat-value" style={{color: stats.unassignedCount > 0 ? 'var(--warning, #f59e0b)' : 'inherit'}}>{stats.unassignedCount}</div></div>
        {stats.byStatus.map(s => (
          <div className="stat-card" key={s.status}><div className="stat-label">{STATUS_MAP[s.status]}</div><div className="stat-value">{s.count}</div></div>
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: 20 }}>
        <div className="card">
          <div className="card-header"><h3 className="card-title">Nach Firma</h3></div>
          <div className="card-body">
            {stats.byCompany.map(c => (
              <div key={c.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                <span>{c.name}</span><span style={{ fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{c.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="card-header"><h3 className="card-title">Letzte Änderungen</h3></div>
          <div className="card-body">
            {stats.recentItems.map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                onClick={() => navigate(`/items/${item.id}`)}>
                <div><div style={{ fontWeight: 500 }}>{item.name}</div><div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{item.company_name}</div></div>
                <span className={`badge badge-${item.status}`}>{STATUS_MAP[item.status]}</span>
              </div>
            ))}
            {stats.recentItems.length === 0 && <p style={{color:'var(--text-muted)',textAlign:'center',padding:20}}>Noch keine Items vorhanden</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

// === Items List ===
function ItemsListPage() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [companies, setCompanies] = useState([]);
  const [categories, setCategories] = useState([]);
  const [rooms, setRooms] = useState([]);
  const searchParams = new URLSearchParams(useLocation().search);
  const [filters, setFilters] = useState({ search: '', company_id: '', status: '', category_id: '', current_room_id: '', target_room_id: '', unassigned: searchParams.get('unassigned') || '' });
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortField, setSortField] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const canEdit = user?.role === 'admin' || user?.role === 'editor';
  const isAdmin = user?.role === 'admin';

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...filters, page, limit: 50, sort: sortField || undefined, dir: sortField ? sortDir : undefined };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const res = await api.get('/items', { params });
      setItems(res.data.items); setTotal(res.data.total); setTotalPages(res.data.totalPages);
    } catch { toast('Fehler beim Laden', 'error'); }
    finally { setLoading(false); }
  }, [filters, page, sortField, sortDir, toast]);

  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => {
    api.get('/companies').then(r => setCompanies(r.data));
    api.get('/categories').then(r => setCategories(r.data));
    api.get('/rooms').then(r => setRooms(r.data));
  }, []);

  const handleExport = async (format) => {
    try {
      const params = { ...filters };
      Object.keys(params).forEach(k => !params[k] && delete params[k]);
      const res = await api.get(`/items/export/${format}`, { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const stamp = new Date().toISOString().slice(0, 10);
      const ext = format === 'zip' ? 'zip' : format === 'csv' ? 'csv' : 'xlsx';
      const a = document.createElement('a'); a.href = url; a.download = `inventar-export-${stamp}.${ext}`; a.click();
      URL.revokeObjectURL(url); toast('Export erfolgreich');
    } catch { toast('Export fehlgeschlagen', 'error'); }
  };

  const handleBackup = () => {
    toast('Backup wird vorbereitet…');
    // Direkter Browser-Download: vermeidet Chrome-".crdownload"-Hänger
    // bei chunked-gestreamten tar.gz-Antworten ohne Content-Length.
    const a = document.createElement('a');
    a.href = '/api/admin/backup';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handleBulkDelete = async () => {
    if (!selected.length) { toast('Bitte Items auswählen', 'error'); return; }
    if (!window.confirm(`${selected.length} Item(s) in den Papierkorb verschieben?`)) return;
    try {
      const res = await api.post('/items/bulk-delete', { ids: selected });
      toast(`${res.data.deleted} Item(s) in Papierkorb verschoben`);
      setSelected([]);
      fetchItems();
    } catch (err) { toast(err.response?.data?.error || 'Löschen fehlgeschlagen', 'error'); }
  };

  const toggleSelect = (id) => setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelected(prev => prev.length === items.length ? [] : items.map(i => i.id));
  const handleSort = (field) => {
    if (sortField === field) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); }
    else { setSortField(field); setSortDir('asc'); }
    setPage(1);
  };
  const SortHeader = ({ field, children }) => (
    <th style={{cursor:'pointer',userSelect:'none',whiteSpace:'nowrap'}} onClick={() => handleSort(field)}>
      {children} {sortField === field ? (sortDir === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  return (
    <div className="page-container">
      <div className="page-header">
        <div><h1 className="page-title">Inventar</h1><p className="page-subtitle">{total} Items</p></div>
        <div className="btn-group">
          {canEdit && <button className="btn btn-primary" onClick={() => navigate('/items/new')}><Icons.Plus /> Neues Item</button>}
          <button className="btn btn-secondary" onClick={() => handleExport('xlsx')}><Icons.Download /> Excel</button>
          <button className="btn btn-secondary" onClick={() => handleExport('csv')}><Icons.Download /> CSV</button>
          <button className="btn btn-secondary" onClick={() => handleExport('zip')} title="CSV + Fotos als ZIP"><Icons.Download /> ZIP (mit Fotos)</button>
          {isAdmin && <button className="btn btn-secondary" onClick={handleBackup} title="Komplett-Backup (DB + Fotos) als tar.gz"><Icons.Download /> Backup</button>}
          {isAdmin && selected.length > 0 && <button className="btn btn-danger" onClick={handleBulkDelete}><Icons.Trash /> Löschen ({selected.length})</button>}
        </div>
      </div>

      <div className="filters-bar">
        <div className="search-input-wrapper"><Icons.Search />
          <input className="form-input" placeholder="Suche nach Name, Barcode…" value={filters.search} onChange={e => { setFilters(f => ({...f, search: e.target.value})); setPage(1); }} />
        </div>
        <select className="form-select" value={filters.company_id} onChange={e => { setFilters(f => ({...f, company_id: e.target.value})); setPage(1); }}>
          <option value="">Alle Firmen</option>{companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="form-select" value={filters.status} onChange={e => { setFilters(f => ({...f, status: e.target.value})); setPage(1); }}>
          <option value="">Alle Status</option>{Object.entries(STATUS_MAP).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select className="form-select" value={filters.category_id} onChange={e => { setFilters(f => ({...f, category_id: e.target.value})); setPage(1); }}>
          <option value="">Alle Kategorien</option>{categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select className="form-select" value={filters.current_room_id} onChange={e => { setFilters(f => ({...f, current_room_id: e.target.value})); setPage(1); }}>
          <option value="">Alle akt. Räume</option>{rooms.filter(r => r.type === 'alt').map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
        <select className="form-select" value={filters.target_room_id} onChange={e => { setFilters(f => ({...f, target_room_id: e.target.value, unassigned: ''})); setPage(1); }}>
          <option value="">Alle Zielräume</option>{rooms.filter(r => r.type === 'neu').map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>

      {loading ? <div className="loading-page"><div className="spinner" /></div> : <>
        <div className="table-container">
          <table className="table"><thead><tr>
            {isAdmin && <th className="checkbox-cell"><input type="checkbox" onChange={toggleSelectAll} checked={selected.length === items.length && items.length > 0} /></th>}
            <th>Foto</th><SortHeader field="name">Name</SortHeader><SortHeader field="barcode">Barcode</SortHeader><SortHeader field="company">Firma</SortHeader><SortHeader field="category">Kategorie</SortHeader><SortHeader field="current_room">Akt. Raum</SortHeader><SortHeader field="target_room">Zielraum</SortHeader><SortHeader field="status">Status</SortHeader><SortHeader field="purchase_price">Kaufpreis</SortHeader>
          </tr></thead><tbody>
            {items.length === 0 ? <tr><td colSpan={isAdmin ? 10 : 9} style={{textAlign:'center',padding:40,color:'var(--text-muted)'}}>Keine Items gefunden</td></tr>
            : items.map(item => (
              <tr key={item.id} className="table-row-clickable" onClick={() => navigate(`/items/${item.id}`)}>
                {isAdmin && <td className="checkbox-cell" onClick={e => e.stopPropagation()}><input type="checkbox" checked={selected.includes(item.id)} onChange={() => toggleSelect(item.id)} /></td>}
                <td>{item.photo ? <img src={`/api/uploads/${item.photo}`} alt="" className="item-photo-thumb" /> :
                  <div style={{width:40,height:40,borderRadius:'var(--radius-sm)',background:'var(--bg-tertiary)',display:'flex',alignItems:'center',justifyContent:'center'}}><Icons.Package style={{width:18,height:18,color:'var(--text-muted)'}} /></div>}</td>
                <td style={{fontWeight:500}}>{item.name}</td>
                <td><span className="barcode-text">{item.barcode || '–'}</span></td>
                <td>{item.company_name}</td>
                <td>{item.category_name || '–'}</td>
                <td>{item.current_room_name || '–'}</td>
                <td>{item.target_room_name || '–'}</td>
                <td><span className={`badge badge-${item.status}`}>{STATUS_MAP[item.status]}</span></td>
                <td style={{fontFamily:'var(--font-mono)'}}>{item.purchase_price ? `${Number(item.purchase_price).toLocaleString('de-DE', {minimumFractionDigits:2})} €` : '–'}</td>
              </tr>
            ))}
          </tbody></table>
        </div>
        {totalPages > 1 && <div className="pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}><Icons.ChevronLeft style={{width:14,height:14}} /></button>
          {Array.from({length: Math.min(totalPages, 7)}, (_, i) => {
            let p; if (totalPages <= 7) p = i + 1; else if (page <= 4) p = i + 1; else if (page >= totalPages - 3) p = totalPages - 6 + i; else p = page - 3 + i;
            return <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>;
          })}
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><Icons.ChevronRight style={{width:14,height:14}} /></button>
          <span className="pagination-info">{total} Items</span>
        </div>}
      </>}
    </div>
  );
}

// === Item Detail ===
function ItemDetailPage() {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [history, setHistory] = useState([]);
  const [tab, setTab] = useState('details');
  const [rotating, setRotating] = useState(false);
  const [photoCacheBust, setPhotoCacheBust] = useState(0);
  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  useEffect(() => {
    api.get(`/items/${id}`).then(r => setItem(r.data)).catch(() => navigate('/items'));
    api.get(`/items/${id}/history`).then(r => setHistory(r.data));
  }, [id, navigate]);

  const handleDelete = async () => {
    if (!window.confirm('Item in den Papierkorb verschieben?')) return;
    try { await api.delete(`/items/${id}`); toast('Item in Papierkorb verschoben'); navigate('/items'); }
    catch { toast('Löschen fehlgeschlagen', 'error'); }
  };

  const handleRotate = async () => {
    setRotating(true);
    try {
      await api.post(`/items/${id}/rotate`);
      setPhotoCacheBust(Date.now());
      toast('Foto gedreht');
    } catch (err) {
      toast(err.response?.data?.error || 'Drehen fehlgeschlagen', 'error');
    } finally {
      setRotating(false);
    }
  };

  if (!item) return <div className="page-container"><div className="loading-page"><div className="spinner" /></div></div>;
  const fmt = (n) => n ? `${Number(n).toLocaleString('de-DE', {minimumFractionDigits:2})} €` : '–';

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{display:'flex',alignItems:'center',gap:12}}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/items')}><Icons.ChevronLeft /> Zurück</button>
          <div><h1 className="page-title">{item.name}</h1><p className="page-subtitle">{item.barcode ? `Barcode: ${item.barcode}` : 'Kein Barcode verknüpft'}</p></div>
        </div>
        <div className="btn-group">
          {canEdit && <button className="btn btn-primary" onClick={() => navigate(`/items/${id}/edit`)}><Icons.Edit /> Bearbeiten</button>}
          {user?.role === 'admin' && <button className="btn btn-danger" onClick={handleDelete}><Icons.Trash /> Löschen</button>}
        </div>
      </div>
      <div className="tabs">
        <button className={`tab ${tab === 'details' ? 'active' : ''}`} onClick={() => setTab('details')}>Details</button>
        <button className={`tab ${tab === 'history' ? 'active' : ''}`} onClick={() => setTab('history')}>Historie ({history.length})</button>
      </div>
      {tab === 'details' ? (
        <div className="card"><div className="card-body"><div className="detail-grid"><div>
          <div className="detail-field"><div className="detail-label">Name</div><div className="detail-value">{item.name}</div></div>
          <div className="detail-field"><div className="detail-label">Barcode</div><div className="detail-value">{item.barcode || 'Nicht verknüpft'}</div></div>
          <div className="detail-field"><div className="detail-label">Serien-/Gerätenummer</div><div className="detail-value">{item.serial_number || '–'}</div></div>
          <div className="detail-field"><div className="detail-label">Firma</div><div className="detail-value">{item.company_name}</div></div>
          <div className="detail-field"><div className="detail-label">Kategorie</div><div className="detail-value">{item.category_name || '–'}</div></div>
          <div className="detail-field"><div className="detail-label">Status</div><div className="detail-value"><span className={`badge badge-${item.status}`}>{STATUS_MAP[item.status]}</span></div></div>
          {item.status === 'lent' && <div className="detail-field"><div className="detail-label">Verliehen an</div><div className="detail-value">{item.lent_to || '–'}</div></div>}
          <div className="detail-field"><div className="detail-label">Aktueller Raum</div><div className="detail-value">{item.current_room_name || '–'}</div></div>
          <div className="detail-field"><div className="detail-label">Zielraum</div><div className="detail-value">{item.target_room_name || '–'}</div></div>
          {item.dimensions && <div className="detail-field"><div className="detail-label">Abmessungen</div><div className="detail-value">{item.dimensions}</div></div>}
          {item.comment && <div className="detail-field"><div className="detail-label">Kommentar</div><div className="detail-value">{item.comment}</div></div>}
          <div className="detail-field"><div className="detail-label">Kaufdatum</div><div className="detail-value">{item.purchase_date || '–'}</div></div>
          <div className="detail-field"><div className="detail-label">Kaufpreis</div><div className="detail-value">{fmt(item.purchase_price)}</div></div>
          <div className="detail-field"><div className="detail-label">Anlagevermögen</div><div className="detail-value">{item.is_asset === 1 ? `Ja – ${item.asset_company_name || '–'}` : item.is_asset === 0 ? 'Nein' : 'Nicht geprüft'}</div></div>
          {item.note_accounting && <div className="detail-field"><div className="detail-label">Notiz Buchhaltung</div><div className="detail-value">{item.note_accounting}</div></div>}
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:12,alignItems:'center'}}>
          {item.photo && (
            <>
              <img src={`/api/uploads/${item.photo}${photoCacheBust ? `?v=${photoCacheBust}` : ''}`} alt={item.name} style={{maxWidth:'100%',maxHeight:300,borderRadius:'var(--radius)',objectFit:'contain'}} />
              {canEdit && (
                <button className="btn btn-ghost btn-sm" onClick={handleRotate} disabled={rotating} title="Foto 90° im Uhrzeigersinn drehen">
                  <Icons.Rotate /> {rotating ? 'Drehe…' : '90° drehen'}
                </button>
              )}
            </>
          )}
        </div></div></div></div>
      ) : (
        <div className="card"><div className="card-body">
          {history.length === 0 ? <p style={{color:'var(--text-muted)',textAlign:'center',padding:20}}>Keine Historie vorhanden</p> :
          <ul className="history-list">{history.map(h => (
            <li key={h.id} className="history-item"><div className="history-dot" /><div>
              <div><span className="history-user">{h.user_name}</span> – {h.action === 'created' ? 'Erstellt' : h.action === 'deleted' ? 'In Papierkorb verschoben' : h.action === 'restored' ? 'Wiederhergestellt' : h.field_name && `${h.field_name} geändert`}</div>
              {h.old_value && h.new_value && <div style={{fontSize:'0.8rem',color:'var(--text-secondary)'}}><span style={{textDecoration:'line-through',opacity:0.6}}>{h.old_value}</span> → {h.new_value}</div>}
              {!h.old_value && h.new_value && <div style={{fontSize:'0.8rem',color:'var(--text-secondary)'}}>{h.new_value}</div>}
              <div className="history-time">{new Date(h.created_at).toLocaleString('de-DE')}</div>
            </div></li>
          ))}</ul>}
        </div></div>
      )}
    </div>
  );
}

// === Global Change History (Admin) ===
function HistoryPage({ toast }) {
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/items/history/all', { params: { page, limit: 100 } });
      setHistory(res.data.history); setTotal(res.data.total); setTotalPages(res.data.totalPages);
    } catch { toast('Fehler beim Laden der Historie', 'error'); }
    finally { setLoading(false); }
  }, [page, toast]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  return (
    <div className="page-container">
      <div className="page-header">
        <div><h1 className="page-title">Änderungshistorie</h1><p className="page-subtitle">{total} Einträge</p></div>
      </div>

      {loading ? <div className="loading-page"><div className="spinner" /></div> : <>
        <div className="table-container">
          <table className="table"><thead><tr>
            <th>Zeitpunkt</th><th>Benutzer</th><th>Item</th><th>Aktion</th><th>Details</th>
          </tr></thead><tbody>
            {history.length === 0 ? <tr><td colSpan={5} style={{textAlign:'center',padding:40,color:'var(--text-muted)'}}>Keine Einträge vorhanden</td></tr>
            : history.map(h => (
              <tr key={h.id} className="table-row-clickable" onClick={() => h.item_id && navigate(`/items/${h.item_id}`)}>
                <td style={{whiteSpace:'nowrap',fontSize:'0.85rem'}}>{new Date(h.created_at).toLocaleString('de-DE')}</td>
                <td style={{fontWeight:500}}>{h.user_name || '–'}</td>
                <td>
                  <span style={{color:'var(--primary)'}}>{h.item_name || `Item #${h.item_id}`}</span>
                </td>
                <td>{h.action === 'created' ? <span className="badge badge-available">Erstellt</span> : h.action === 'deleted' ? <span className="badge badge-defect">Gelöscht</span> : h.action === 'restored' ? <span className="badge badge-available">Wiederhergestellt</span> : h.field_name ? <span>{h.field_name}</span> : h.action}</td>
                <td style={{fontSize:'0.85rem'}}>
                  {h.action === 'created' ? (
                    <span style={{color:'var(--text-secondary)'}}>{h.new_value}</span>
                  ) : h.old_value && h.new_value ? (
                    <span><span style={{textDecoration:'line-through',opacity:0.5}}>{h.old_value}</span> → {h.new_value}</span>
                  ) : h.new_value ? (
                    <span>{h.new_value}</span>
                  ) : '–'}
                </td>
              </tr>
            ))}
          </tbody></table>
        </div>
        {totalPages > 1 && <div className="pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}><Icons.ChevronLeft style={{width:14,height:14}} /></button>
          {Array.from({length: Math.min(totalPages, 7)}, (_, i) => {
            let p; if (totalPages <= 7) p = i + 1; else if (page <= 4) p = i + 1; else if (page >= totalPages - 3) p = totalPages - 6 + i; else p = page - 3 + i;
            return <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>;
          })}
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><Icons.ChevronRight style={{width:14,height:14}} /></button>
          <span className="pagination-info">{total} Einträge</span>
        </div>}
      </>}
    </div>
  );
}

// === Papierkorb (Trash) ===
function TrashPage({ toast }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchTrash = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/items/trash', { params: { page, limit: 50 } });
      setItems(res.data.items); setTotal(res.data.total); setTotalPages(res.data.totalPages);
    } catch { toast('Fehler beim Laden des Papierkorbs', 'error'); }
    finally { setLoading(false); }
  }, [page, toast]);

  useEffect(() => { fetchTrash(); }, [fetchTrash]);

  const handleRestore = async (id) => {
    try {
      await api.post(`/items/${id}/restore`);
      toast('Item wiederhergestellt');
      fetchTrash();
    } catch (err) { toast(err.response?.data?.error || 'Wiederherstellen fehlgeschlagen', 'error'); }
  };

  const handlePermanentDelete = async (id) => {
    if (!window.confirm('Item endgültig löschen? Das kann nicht rückgängig gemacht werden!')) return;
    try {
      await api.delete(`/items/${id}/permanent`);
      toast('Item endgültig gelöscht');
      fetchTrash();
    } catch (err) { toast(err.response?.data?.error || 'Löschen fehlgeschlagen', 'error'); }
  };

  const handlePurgeAll = async () => {
    if (!window.confirm(`Papierkorb leeren? Alle ${total} Items werden endgültig gelöscht!`)) return;
    try {
      await api.post('/items/trash/purge-all');
      toast('Papierkorb geleert');
      fetchTrash();
    } catch (err) { toast(err.response?.data?.error || 'Fehler', 'error'); }
  };

  const daysRemaining = (dateStr) => {
    const deleted = new Date(dateStr);
    const now = new Date();
    const daysPassed = Math.floor((now - deleted) / (1000 * 60 * 60 * 24));
    return Math.max(0, 30 - daysPassed);
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div><h1 className="page-title">Papierkorb</h1><p className="page-subtitle">{total} gelöschte Items – automatische Löschung nach 30 Tagen</p></div>
        {total > 0 && <button className="btn btn-danger" onClick={handlePurgeAll}><Icons.Trash /> Papierkorb leeren</button>}
      </div>

      {loading ? <div className="loading-page"><div className="spinner" /></div> : <>
        <div className="table-container">
          <table className="table"><thead><tr>
            <th>Name</th><th>Firma</th><th>Kategorie</th><th>Gelöscht am</th><th>Verbleibend</th><th></th>
          </tr></thead><tbody>
            {items.length === 0 ? <tr><td colSpan={6} style={{textAlign:'center',padding:40,color:'var(--text-muted)'}}>Papierkorb ist leer</td></tr>
            : items.map(item => {
              const remaining = daysRemaining(item.deleted_at);
              return (
                <tr key={item.id}>
                  <td style={{fontWeight:500}}>{item.name}</td>
                  <td>{item.company_name}</td>
                  <td>{item.category_name || '–'}</td>
                  <td style={{fontSize:'0.85rem'}}>{new Date(item.deleted_at).toLocaleString('de-DE')}</td>
                  <td><span style={{color: remaining <= 7 ? 'var(--danger)' : 'var(--text-muted)', fontSize:'0.85rem'}}>{remaining} Tage</span></td>
                  <td>
                    <div className="btn-group">
                      <button className="btn btn-primary btn-sm" onClick={() => handleRestore(item.id)}><Icons.RotateCcw style={{width:14,height:14}} /> Wiederherstellen</button>
                      <button className="btn btn-danger btn-sm" onClick={() => handlePermanentDelete(item.id)}><Icons.Trash style={{width:14,height:14}} /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody></table>
        </div>
        {totalPages > 1 && <div className="pagination">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}><Icons.ChevronLeft style={{width:14,height:14}} /></button>
          {Array.from({length: Math.min(totalPages, 7)}, (_, i) => {
            let p; if (totalPages <= 7) p = i + 1; else if (page <= 4) p = i + 1; else if (page >= totalPages - 3) p = totalPages - 6 + i; else p = page - 3 + i;
            return <button key={p} className={p === page ? 'active' : ''} onClick={() => setPage(p)}>{p}</button>;
          })}
          <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}><Icons.ChevronRight style={{width:14,height:14}} /></button>
          <span className="pagination-info">{total} Items</span>
        </div>}
      </>}
    </div>
  );
}

// === Wired Page Wrappers ===
function WiredItemForm() { const toast = useToast(); return <ItemFormPage toast={toast} />; }
function WiredScanner() { const toast = useToast(); return <ScannerPage toast={toast} />; }
function WiredUsers() { const toast = useToast(); return <UsersPage toast={toast} />; }
function WiredCategories() { const toast = useToast(); return <CategoriesPage toast={toast} />; }
function WiredHistory() { const toast = useToast(); return <HistoryPage toast={toast} />; }
function WiredTrash() { const toast = useToast(); return <TrashPage toast={toast} />; }
function WiredMoveList() { return <MoveListPage />; }

// === Layout ===
function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { path: '/', label: 'Dashboard', icon: Icons.Dashboard },
    { path: '/items', label: 'Inventar', icon: Icons.Package },
    { path: '/scanner', label: 'Scanner', icon: Icons.Scan },
    { path: '/umzugsliste', label: 'Umzugslisten', icon: Icons.MapPin },
    { path: '/raumplan', label: 'Raumplan', icon: Icons.FloorPlan },
  ];
  const adminItems = [
    { path: '/users', label: 'Benutzer', icon: Icons.Users },
    { path: '/categories', label: 'Kategorien', icon: Icons.Tag },
    { path: '/history', label: 'Änderungshistorie', icon: Icons.Clock },
    { path: '/trash', label: 'Papierkorb', icon: Icons.Trash },
  ];
  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  return (
    <div className="app-layout">
      <button className="mobile-toggle" onClick={() => setSidebarOpen(!sidebarOpen)}>{sidebarOpen ? <Icons.X /> : <Icons.Menu />}</button>
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header"><div className="sidebar-logo"><Icons.Package /> Inventar</div></div>
        <nav className="sidebar-nav">
          <div className="nav-section-title">Navigation</div>
          {navItems.map(item => (
            <button key={item.path} className={`nav-item ${isActive(item.path) ? 'active' : ''}`} onClick={() => { navigate(item.path); setSidebarOpen(false); }}><item.icon /> {item.label}</button>
          ))}
          {user?.role === 'admin' && <>
            <div className="nav-section-title">Verwaltung</div>
            {adminItems.map(item => (
              <button key={item.path} className={`nav-item ${isActive(item.path) ? 'active' : ''}`} onClick={() => { navigate(item.path); setSidebarOpen(false); }}><item.icon /> {item.label}</button>
            ))}
          </>}
        </nav>
        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">{user?.name?.[0]?.toUpperCase()}</div>
            <div className="user-details"><div className="user-name">{user?.name}</div><div className="user-role">{ROLE_LABELS[user?.role]}</div></div>
          </div>
          <button className="nav-item" onClick={logout}><Icons.LogOut /> Abmelden</button>
        </div>
      </aside>
      <main className="main-content" onClick={() => sidebarOpen && setSidebarOpen(false)}>
        <Routes>
          <Route path="/" element={<DashboardPage />} />
          <Route path="/items" element={<ItemsListPage />} />
          <Route path="/items/new" element={<ProtectedRoute roles={['admin','editor']}><WiredItemForm /></ProtectedRoute>} />
          <Route path="/items/:id" element={<ItemDetailPage />} />
          <Route path="/items/:id/edit" element={<ProtectedRoute roles={['admin','editor']}><WiredItemForm /></ProtectedRoute>} />
          <Route path="/scanner" element={<WiredScanner />} />
          <Route path="/umzugsliste" element={<WiredMoveList />} />
          <Route path="/raumplan" element={<FloorPlanPage />} />
          <Route path="/users" element={<ProtectedRoute roles={['admin']}><WiredUsers /></ProtectedRoute>} />
          <Route path="/categories" element={<ProtectedRoute roles={['admin']}><WiredCategories /></ProtectedRoute>} />
          <Route path="/history" element={<ProtectedRoute roles={['admin']}><WiredHistory /></ProtectedRoute>} />
          <Route path="/trash" element={<ProtectedRoute roles={['admin']}><WiredTrash /></ProtectedRoute>} />
        </Routes>
      </main>
    </div>
  );
}

// === Umzugslisten (Authenticated) ===
function RoomNotes({ room, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState(room.notes || '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/rooms/${room.id}`, { notes });
      onUpdate(room.id, notes);
      setEditing(false);
    } catch { /* ignore */ }
    finally { setSaving(false); }
  };

  const cancel = () => { setNotes(room.notes || ''); setEditing(false); };

  if (editing) {
    return (
      <div className="movelist-room-notes movelist-room-notes-editing">
        <textarea
          className="movelist-notes-input"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          placeholder="Notiz für diesen Raum…"
          autoFocus
          rows={2}
        />
        <div className="movelist-notes-actions">
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? '…' : 'Speichern'}</button>
          <button className="btn btn-ghost btn-sm" onClick={cancel}>Abbrechen</button>
        </div>
      </div>
    );
  }

  return (
    <div className="movelist-room-notes movelist-room-notes-clickable" onClick={() => setEditing(true)}>
      {room.notes || <span className="movelist-notes-placeholder">Notiz hinzufügen…</span>}
    </div>
  );
}

function MoveListPage() {
  const [items, setItems] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [view, setView] = useState('target'); // 'target' or 'current'
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/items', { params: { limit: 9999 } }),
      api.get('/rooms')
    ]).then(([itemsRes, roomsRes]) => {
      setItems(itemsRes.data.items);
      setRooms(roomsRes.data);
      setLoading(false);
    });
  }, []);

  const handleRoomNotesUpdate = (roomId, notes) => {
    setRooms(prev => prev.map(r => r.id === roomId ? { ...r, notes } : r));
  };

  if (loading) return <div className="page-container"><div className="loading-page"><div className="spinner" /></div></div>;

  const roomType = view === 'target' ? 'neu' : 'alt';
  const roomField = view === 'target' ? 'target_room_id' : 'current_room_id';
  const roomNameField = view === 'target' ? 'target_room_name' : 'current_room_name';
  const filteredRooms = rooms.filter(r => r.type === roomType);

  const grouped = {};
  for (const room of filteredRooms) {
    grouped[room.id] = { room, items: [] };
  }
  const unassigned = [];
  for (const item of items) {
    const rid = item[roomField];
    if (rid && grouped[rid]) {
      grouped[rid].items.push(item);
    } else if (!rid) {
      unassigned.push(item);
    }
  }

  return (
    <div className="page-container">
      <div className="page-header no-print">
        <div>
          <h1 className="page-title">Umzugslisten</h1>
          <p className="page-subtitle">{items.length} Items, {view === 'target' ? 'nach Zielraum' : 'nach aktuellem Raum'}</p>
        </div>
        <div className="btn-group">
          <button className="btn btn-primary" onClick={() => window.print()}>Drucken</button>
        </div>
      </div>

      <div className="tabs no-print" style={{marginBottom: 24}}>
        <button className={`tab ${view === 'target' ? 'active' : ''}`} onClick={() => setView('target')}>Nach Zielraum</button>
        <button className={`tab ${view === 'current' ? 'active' : ''}`} onClick={() => setView('current')}>Nach aktuellem Raum</button>
      </div>

      <div className="print-title">Umzugsliste – {view === 'target' ? 'Nach Zielraum' : 'Nach aktuellem Raum'}</div>

      {filteredRooms.map(room => {
        const roomItems = grouped[room.id]?.items || [];
        if (roomItems.length === 0) return null;
        return (
          <div key={room.id} className="movelist-room">
            <div className="movelist-room-header">
              <h2 className="movelist-room-name">{room.name}</h2>
              <span className="movelist-room-count">{roomItems.length} Items</span>
            </div>
            {room.type === 'neu' && view === 'target' && (
              <RoomNotes room={room} onUpdate={handleRoomNotesUpdate} />
            )}
            <div className="movelist-items">
              {roomItems.map(item => (
                <div key={item.id} className="movelist-item">
                  {item.photo && <img src={`/api/uploads/${item.photo}`} alt="" className="movelist-item-photo" />}
                  <div className="movelist-item-info">
                    <div className="movelist-item-name">{item.name}</div>
                    {view === 'current' && item.target_room_name && (
                      <div className="movelist-item-target">→ {item.target_room_name}</div>
                    )}
                    {view === 'target' && item.current_room_name && (
                      <div className="movelist-item-source">aus: {item.current_room_name}</div>
                    )}
                    {item.dimensions && <div className="movelist-item-dim">{item.dimensions}</div>}
                    {item.comment && <div className="movelist-item-comment">{item.comment}</div>}
                    {item.barcode && <div className="movelist-item-barcode">#{item.barcode}</div>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {unassigned.length > 0 && (
        <div className="movelist-room">
          <div className="movelist-room-header">
            <h2 className="movelist-room-name" style={{color:'var(--warning)'}}>Noch zuzuordnen</h2>
            <span className="movelist-room-count">{unassigned.length} Items</span>
          </div>
          <div className="movelist-items">
            {unassigned.map(item => (
              <div key={item.id} className="movelist-item">
                {item.photo && <img src={`/api/uploads/${item.photo}`} alt="" className="movelist-item-photo" />}
                <div className="movelist-item-info">
                  <div className="movelist-item-name">{item.name}</div>
                  {item.dimensions && <div className="movelist-item-dim">{item.dimensions}</div>}
                  {item.comment && <div className="movelist-item-comment">{item.comment}</div>}
                  {item.barcode && <div className="movelist-item-barcode">#{item.barcode}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// === Umzug (Public) ===
function UmzugPage() {
  const [code, setCode] = useState('');
  const [item, setItem] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [scanCandidate, setScanCandidate] = useState(null);
  const videoRef = React.useRef(null);
  const streamRef = React.useRef(null);
  const animRef = React.useRef(null);
  const scanActiveRef = React.useRef(false);
  const cropCanvasRef = React.useRef(null);

  const stopScanner = useCallback(() => {
    scanActiveRef.current = false;
    if (animRef.current) { cancelAnimationFrame(animRef.current); animRef.current = null; }
    if (videoRef.current) { videoRef.current.srcObject = null; }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    setScanning(false);
  }, []);

  const lookup = useCallback(async (barcode) => {
    setLoading(true); setError(''); setItem(null); setScanCandidate(null);
    try {
      const res = await api.get(`/items/public/barcode/${barcode.trim()}`);
      setItem(res.data);
    } catch {
      setError('Kein Item mit dieser Nummer gefunden.');
    } finally { setLoading(false); }
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    lookup(code.trim());
  };

  const getCenterCrop = (video) => {
    if (!cropCanvasRef.current) cropCanvasRef.current = document.createElement('canvas');
    const canvas = cropCanvasRef.current;
    const cropW = Math.floor(video.videoWidth * 0.5);
    const cropH = Math.floor(video.videoHeight * 0.4);
    const sx = Math.floor((video.videoWidth - cropW) / 2);
    const sy = Math.floor((video.videoHeight - cropH) / 2);
    canvas.width = cropW;
    canvas.height = cropH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, sx, sy, cropW, cropH, 0, 0, cropW, cropH);
    return canvas;
  };

  const startScanner = async () => {
    stopScanner();
    setError(''); setItem(null); setScanCandidate(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
      });
      streamRef.current = stream;
      scanActiveRef.current = true;
      setScanning(true);
      await new Promise(r => setTimeout(r, 50));
      if (!scanActiveRef.current || !videoRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      const video = videoRef.current;
      video.srcObject = stream;
      video.setAttribute('playsinline', 'true');
      video.muted = true;
      await video.play();
      if (!scanActiveRef.current) return;

      if ('BarcodeDetector' in window) {
        const detector = new window.BarcodeDetector({ formats: ['code_128', 'code_39', 'ean_13', 'ean_8'] });
        const scan = () => {
          if (!scanActiveRef.current || !videoRef.current) return;
          if (videoRef.current.readyState < 2) { animRef.current = requestAnimationFrame(scan); return; }
          const cropped = getCenterCrop(videoRef.current);
          detector.detect(cropped).then(barcodes => {
            if (!scanActiveRef.current) return;
            if (barcodes.length > 0) { stopScanner(); setScanCandidate(barcodes[0].rawValue); return; }
            animRef.current = requestAnimationFrame(scan);
          }).catch(() => {
            if (!scanActiveRef.current) return;
            setTimeout(() => { if (scanActiveRef.current) animRef.current = requestAnimationFrame(scan); }, 200);
          });
        };
        animRef.current = requestAnimationFrame(scan);
      } else {
        try {
          const { BrowserMultiFormatReader } = await import('@zxing/library');
          if (!scanActiveRef.current) return;
          const reader = new BrowserMultiFormatReader();
          reader.decodeFromVideoDevice(null, video, (res) => {
            if (res && scanActiveRef.current) { stopScanner(); setScanCandidate(res.getText()); }
          });
        } catch { setError('Scanner nicht unterstützt. Bitte Nummer eintippen.'); stopScanner(); }
      }
    } catch { setError('Kamera-Zugriff verweigert.'); }
  };

  useEffect(() => () => stopScanner(), [stopScanner]);

  return (
    <div className="umzug-page">
      <div className="umzug-header">
        <h1>Umzug</h1>
        <p>Barcode-Nummer eingeben oder scannen</p>
      </div>

      <form onSubmit={handleSubmit} className="umzug-input">
        <input
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          placeholder="z.B. 00042"
          value={code}
          onChange={e => setCode(e.target.value)}
          className="umzug-code-input"
          autoFocus
        />
        <button type="submit" className="umzug-btn" disabled={loading}>
          {loading ? 'Suche…' : 'Suchen'}
        </button>
      </form>

      <div className="umzug-scanner-section">
        {scanning ? (
          <div>
            <div style={{position:'relative',display:'inline-block'}}>
              <video ref={videoRef} style={{width:'100%',maxWidth:500,borderRadius:12}} playsInline muted />
              <div style={{position:'absolute',top:'30%',left:'25%',right:'25%',bottom:'30%',border:'2px solid #3b82f6',borderRadius:4,pointerEvents:'none',opacity:0.7}} />
            </div>
            <br />
            <button className="umzug-btn umzug-btn-stop" onClick={stopScanner}>Stopp</button>
          </div>
        ) : (
          <button className="umzug-btn umzug-btn-scan" onClick={startScanner}>
            Kamera-Scanner starten
          </button>
        )}
      </div>

      {scanCandidate && (
        <div style={{padding:'16px',background:'rgba(59,130,246,0.1)',border:'1px solid rgba(59,130,246,0.3)',borderRadius:12,marginBottom:20,textAlign:'center'}}>
          <div style={{marginBottom:12}}>Erkannt: <strong style={{fontFamily:'monospace',fontSize:'1.3rem'}}>{scanCandidate}</strong></div>
          <div style={{display:'flex',gap:8,justifyContent:'center'}}>
            <button className="umzug-btn" onClick={() => lookup(scanCandidate)}>Suchen</button>
            <button className="umzug-btn" style={{background:'transparent',border:'1px solid rgba(255,255,255,0.3)'}} onClick={() => { setScanCandidate(null); startScanner(); }}>Nochmal scannen</button>
          </div>
        </div>
      )}

      {error && <div className="umzug-error">{error}</div>}

      {item && (
        <div className="umzug-result">
          {item.photo && (
            <img src={`/api/items/public/photo/${item.photo}`} alt={item.name} className="umzug-photo" />
          )}
          <div className="umzug-info">
            <h2 className="umzug-item-name">{item.name}</h2>
            <div className="umzug-target-room">{item.target_room_name || 'Kein Zielraum zugeordnet'}</div>
            {item.dimensions && <div className="umzug-detail"><span className="umzug-detail-label">Maße:</span> {item.dimensions}</div>}
            {item.comment && <div className="umzug-detail"><span className="umzug-detail-label">Hinweis:</span> {item.comment}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// === Raumplan ===
function FloorPlanPage() {
  const { user } = useAuth();
  const addToast = useToast();
  const [pins, setPins] = useState([]);
  const [labelsVisible, setLabelsVisible] = useState(true);
  const [personsVisible, setPersonsVisible] = useState(true);
  const [mobiliarVisible, setMobiliarVisible] = useState(true);
  const [dragData, setDragData] = useState(null);
  const [movingPin, setMovingPin] = useState(null);
  const [moveOffset, setMoveOffset] = useState({ x: 0, y: 0 });
  const [contextMenu, setContextMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const planRef = React.useRef(null);
  const canEdit = user?.role === 'admin' || user?.role === 'editor';

  useEffect(() => {
    api.get('/floor-pins').then(res => { setPins(res.data); setLoading(false); })
      .catch(() => { addToast('Fehler beim Laden der Pins', 'error'); setLoading(false); });
  }, [addToast]);

  useEffect(() => {
    const handleMove = (e) => {
      if (!movingPin || !planRef.current) return;
      const r = planRef.current.getBoundingClientRect();
      const newX = e.clientX - r.left - moveOffset.x;
      const newY = e.clientY - r.top - moveOffset.y;
      setPins(prev => prev.map(p => p.id === movingPin ? { ...p, x: newX, y: newY } : p));
    };
    const handleUp = () => {
      if (movingPin) {
        const pin = pins.find(p => p.id === movingPin);
        if (pin) api.put(`/floor-pins/${pin.id}`, { label: pin.label, x: pin.x, y: pin.y }).catch(() => addToast('Fehler beim Speichern', 'error'));
        setMovingPin(null);
      }
    };
    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleUp);
    return () => { document.removeEventListener('mousemove', handleMove); document.removeEventListener('mouseup', handleUp); };
  }, [movingPin, moveOffset, pins, addToast]);

  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const handleDrop = async (e) => {
    e.preventDefault();
    if (!dragData || !planRef.current) return;
    const r = planRef.current.getBoundingClientRect();
    const x = e.clientX - r.left - 14;
    const y = e.clientY - r.top - 14;
    let label = dragData.label;
    if (dragData.type === 'person') {
      const name = window.prompt('Name der Person:');
      if (!name || !name.trim()) { setDragData(null); return; }
      label = name.trim();
    } else if (dragData.type === 'note') {
      const text = window.prompt('Notiz:');
      if (!text || !text.trim()) { setDragData(null); return; }
      label = text.trim();
    }
    try {
      const res = await api.post('/floor-pins', { type: dragData.type, label, x, y });
      setPins(prev => [...prev, res.data]);
    } catch { addToast('Fehler beim Erstellen', 'error'); }
    setDragData(null);
  };

  const deletePin = async (id) => {
    try {
      await api.delete(`/floor-pins/${id}`);
      setPins(prev => prev.filter(p => p.id !== id));
    } catch { addToast('Fehler beim Löschen', 'error'); }
    setContextMenu(null);
  };

  const editPinLabel = async () => {
    if (!contextMenu) return;
    const pin = pins.find(p => p.id === contextMenu.pinId);
    if (!pin) return;
    const promptText = pin.type === 'person' ? 'Name:' : pin.type === 'note' ? 'Notiz:' : 'Beschriftung:';
    const nl = window.prompt(promptText, pin.label);
    if (nl !== null && nl.trim()) {
      try {
        const res = await api.put(`/floor-pins/${pin.id}`, { label: nl.trim(), x: pin.x, y: pin.y });
        setPins(prev => prev.map(p => p.id === pin.id ? res.data : p));
      } catch { addToast('Fehler beim Aktualisieren', 'error'); }
    }
    setContextMenu(null);
  };

  const clearAllPins = async () => {
    if (!pins.length || !window.confirm(`Alle ${pins.length} Pins entfernen?`)) return;
    try {
      await Promise.all(pins.map(p => api.delete(`/floor-pins/${p.id}`)));
      setPins([]);
    } catch { addToast('Fehler beim Löschen', 'error'); }
  };

  const pinTypes = [
    { type: 'desk', emoji: '🖥', label: 'Schreibtisch', section: 'Mobiliar' },
    { type: 'chair', emoji: '🪑', label: 'Stuhl', section: 'Mobiliar' },
    { type: 'printer', emoji: '🖨', label: 'Drucker', section: 'Mobiliar' },
    { type: 'plant', emoji: '🌿', label: 'Pflanze', section: 'Mobiliar' },
    { type: 'person', emoji: '👤', label: 'Person (Sitzplatz)', section: 'Personen' },
    { type: 'note', emoji: '📝', label: 'Notiz', section: 'Sonstiges' },
  ];

  const pinColors = { desk: '#1e40af', chair: '#7c3aed', printer: '#b45309', plant: '#15803d', person: '#6d28d9', note: '#92400e' };

  const roomLabels = [
    { text: 'B1', x: 162, y: 113 },
    { text: 'WC\nPhysio', x: 255, y: 110 },
    { text: 'Server-\nraum', x: 327, y: 109 },
    { text: 'Eingangstür', x: 382, y: 183 },
    { text: 'Empfang', x: 461, y: 114 },
    { text: 'Raum 1', x: 579, y: 116 },
    { text: 'Raum 2', x: 716, y: 114 },
    { text: 'Raum 3', x: 865, y: 114 },
    { text: 'KGG', x: 166, y: 333 },
    { text: 'Treppenhaus', x: 328, y: 279, rotate: true },
    { text: 'Küche', x: 417, y: 306 },
    { text: 'Konferenzraum', x: 630, y: 358 },
    { text: 'WCs und Dusche', x: 830, y: 333 },
    { text: 'B3', x: 160, y: 537 },
    { text: 'B2', x: 305, y: 536 },
    { text: 'Terrasse', x: 430, y: 557 },
    { text: 'Raum 6', x: 576, y: 542 },
    { text: 'Raum 5', x: 720, y: 537 },
    { text: 'Raum 4', x: 864, y: 537 },
  ];

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}><div className="spinner" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {/* Toolbar */}
      <div className="rp-toolbar">
        <span className="rp-toolbar-title">Gebäude 4 – OG</span>
        <span className="rp-toolbar-sep" />
        {canEdit && pinTypes.map(pt => (
          <div key={pt.type} className="rp-toolbar-pin" draggable
            onDragStart={() => setDragData(pt)}>
            <div className="rp-pin-icon" style={{ background: pinColors[pt.type] }}>{pt.emoji}</div>
            <span className="rp-toolbar-pin-label">{pt.label.replace(' (Sitzplatz)', '')}</span>
          </div>
        ))}
        {canEdit && <span className="rp-toolbar-sep" />}
        <span className="rp-toolbar-info">{pins.length} Pin{pins.length !== 1 ? 's' : ''}</span>
        <span style={{ flex: 1 }} />
        <button className={`rp-toolbar-btn ${personsVisible ? 'active' : ''}`} onClick={() => setPersonsVisible(!personsVisible)}>Personen</button>
        <button className={`rp-toolbar-btn ${mobiliarVisible ? 'active' : ''}`} onClick={() => setMobiliarVisible(!mobiliarVisible)}>Mobiliar</button>
        <button className={`rp-toolbar-btn ${labelsVisible ? 'active' : ''}`} onClick={() => setLabelsVisible(!labelsVisible)}>Raumnamen</button>
        <button className="rp-toolbar-btn" onClick={() => { document.body.classList.add('rp-printing'); setTimeout(() => { window.print(); document.body.classList.remove('rp-printing'); }, 100); }}>PDF / Drucken</button>
        {canEdit && <button className="rp-toolbar-btn" onClick={clearAllPins}>Alle entfernen</button>}
      </div>

        {/* Canvas */}
        <div className="rp-canvas"
          onDragOver={e => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
          onDrop={handleDrop}>
          <div className="rp-floor-plan" ref={planRef} style={{ backgroundImage: `url(${process.env.PUBLIC_URL}/bauplan-hilden.jpeg)` }}>
            {/* Room labels */}
            {labelsVisible && roomLabels.map((rl, i) => (
              <div key={i} className="rp-room-label" style={{
                left: rl.x, top: rl.y,
                transform: rl.rotate ? 'rotate(-90deg)' : undefined,
                transformOrigin: rl.rotate ? 'center' : undefined,
              }}>{rl.text.split('\n').map((line, j) => <React.Fragment key={j}>{j > 0 && <br />}{line}</React.Fragment>)}</div>
            ))}

            {/* Pins */}
            {pins.filter(pin => {
              if (pin.type === 'person') return personsVisible;
              return mobiliarVisible;
            }).map(pin => (
              <div key={pin.id}
                className={`rp-placed-pin ${pin.type === 'person' ? 'rp-person-pin' : ''} ${pin.type === 'note' ? 'rp-note-pin' : ''}`}
                style={{ left: pin.x, top: pin.y, background: pinColors[pin.type] || '#64748b', zIndex: movingPin === pin.id ? 20 : 10 }}
                onMouseDown={canEdit ? (e) => {
                  if (e.target.closest('.rp-pin-delete')) return;
                  e.preventDefault();
                  setMovingPin(pin.id);
                  const pr = e.currentTarget.getBoundingClientRect();
                  setMoveOffset({ x: e.clientX - pr.left, y: e.clientY - pr.top });
                } : undefined}
                onContextMenu={canEdit ? (e) => {
                  e.preventDefault();
                  setContextMenu({ pinId: pin.id, x: e.clientX, y: e.clientY });
                } : undefined}>
                <span style={{ fontSize: pin.type === 'person' || pin.type === 'note' ? 11 : 13 }}>
                  {pinTypes.find(pt => pt.type === pin.type)?.emoji || '📌'}
                </span>
                {pin.type === 'person' && <span className="rp-person-name">{pin.label}</span>}
                {pin.type === 'note' && <span className="rp-note-text">{pin.label.length > 30 ? pin.label.substring(0, 30) + '…' : pin.label}</span>}
                <div className="rp-pin-tooltip">{pin.label || pinTypes.find(pt => pt.type === pin.type)?.label}</div>
                {canEdit && <button className="rp-pin-delete" onClick={() => deletePin(pin.id)}>&times;</button>}
              </div>
            ))}
          </div>
        </div>

      {/* Context menu */}
      {contextMenu && (
        <div className="rp-context-menu" style={{ left: contextMenu.x, top: contextMenu.y }} onClick={e => e.stopPropagation()}>
          <div className="rp-context-item" onClick={editPinLabel}>Beschriftung ändern</div>
          <div className="rp-context-item rp-danger" onClick={() => deletePin(contextMenu.pinId)}>Entfernen</div>
        </div>
      )}
    </div>
  );
}

// === App ===
export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <AppRoutes />
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  const { user } = useAuth();
  return (
    <Routes>
      <Route path="/umzug" element={<UmzugPage />} />
      <Route path="/login" element={user ? <Navigate to="/" /> : <LoginPage />} />
      <Route path="/*" element={user ? <Layout /> : <Navigate to="/login" />} />
    </Routes>
  );
}
